/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/base';
import BookingModel from 'uplisting-frontend/models/booking';
import { BookingStatus } from 'uplisting-frontend/models/schemas';

interface IArgs {
  booking: BookingModel;
}

export interface BookingDetailsSignature {
  Element: null;

  Args: IArgs;
}

export default class UiBookingDetailsComponent extends Component<BookingDetailsSignature> {
  @service router!: Services['router'];
  @service notifications!: Services['notifications'];
  @service('repositories/booking')
  bookingRepository!: Services['repositories/booking'];

  @cached @tracked showCancelModal = false;
  @cached @tracked showDeleteModal = false;

  @cached
  get booking(): BookingModel {
    return this.args.booking;
  }

  @cached
  get changeset(): GenericChangeset<BookingModel> {
    return this.bookingRepository.buildChangeset(this.booking);
  }

  @cached
  get canCancel(): boolean {
    return !this.booking.imported;
  }

  @cached
  get canDelete(): boolean {
    if (this.booking.deletable === false) {
      return false;
    }

    return this.canCancel;
  }

  @cached
  get redirectRoute(): string {
    switch (this.router.currentRouteName) {
      case 'action.bookings.details':
        return 'action.index';
      case 'calendar.bookings.details':
        return 'calendar.index';
      default:
        return '';
    }
  }

  @action
  async handleSaveBooking(): Promise<void> {
    try {
      await this.changeset.save();

      this.notifications.info();
    } catch {
      this.notifications.error();
    }
  }

  @action
  async handleCancelBooking(): Promise<void> {
    this.changeset.status = BookingStatus.cancelled;

    await this.changeset.save();

    await this.redirectAfterAction();
  }

  @action
  async handleDeleteBooking(): Promise<void> {
    await this.booking.destroyRecord();

    await this.redirectAfterAction();
  }

  private async redirectAfterAction(): Promise<void> {
    await this.router.transitionTo(this.redirectRoute);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Details': typeof UiBookingDetailsComponent;
  }
}
